import { Button } from '@progress/kendo-react-buttons';
import { ActionsLayout, Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import React, { createContext, FC, useContext, useReducer, useState } from 'react';
import { MemoryRouter } from 'react-router-dom';

export const DIALOG_FORM_ID = 'DIALOG_FORM';

interface IDialogProps {
    title: string;
    component: JSX.Element;
    isForm?: boolean;
    width?: string;
    height?: string;
    actionButtons?: ActionButtonsType | IActionButtons;
    buttonsLayout?: ActionsLayout;
    customCloseAction?: () => void;
}

export enum ActionButtonsType {
    ConfirmCancel = 'ConfirmCancel',
    YesNo = 'YesNo'
}

const confimText = { [ActionButtonsType.ConfirmCancel]: 'Patvirtinti', [ActionButtonsType.YesNo]: 'Taip' };
const cancelText = { [ActionButtonsType.ConfirmCancel]: 'Atšaukti', [ActionButtonsType.YesNo]: 'Ne' };

export interface IActionButtons {
    confirm?: string;
    cancel?: string;
}

const getConfimText = (actionButtons: ActionButtonsType | IActionButtons) => {
    if (!actionButtons) {
        return confimText[ActionButtonsType.ConfirmCancel];
    }

    if (typeof actionButtons === 'object') {
        return actionButtons.confirm;
    }

    return confimText[actionButtons];
};
const getCancelText = (actionButtons: ActionButtonsType | IActionButtons) => {
    if (!actionButtons) {
        return cancelText[ActionButtonsType.ConfirmCancel];
    }

    if (typeof actionButtons === 'object') {
        return actionButtons.cancel;
    }

    return cancelText[actionButtons];
};

export interface IDialogResult<T = any> {
    success: boolean;
    payload?: T;
}

interface IPromise<IDialogResult> {
    resolve: (value?: IDialogResult) => void;
}

interface IContext {
    showDialogAsync: (dialog: IDialogProps) => Promise<IDialogResult>;
    closeDialog: () => void;
}

interface IDialogState extends IDialogProps {
    show: boolean;
    disabled: boolean;
}

const initialState: IDialogState[] = [];

const SHOW_DIALOG = 'shell/dialogs/SHOW_DIALOG';
const CLOSE_DIALOG = 'shell/dialogs/CLOSE_DIALOG';
const CHANGE_DISABLED = 'shell/dialogs/CHANGE_DISABLED';

const actions = {
    showDialog: (dialog: IDialogProps) => ({
        type: SHOW_DIALOG as typeof SHOW_DIALOG,
        payload: dialog
    }),
    closeDialog: () => ({
        type: CLOSE_DIALOG as typeof CLOSE_DIALOG
    }),
    changeDisabled: (disabled: boolean) => ({
        type: CHANGE_DISABLED as typeof CHANGE_DISABLED,
        payload: disabled
    })
};

type ActionTypes = ActionUnion<typeof actions>;

const reducer = (state: IDialogState[], action: ActionTypes): IDialogState[] => {
    let newState: IDialogState[];
    switch (action.type) {
        case SHOW_DIALOG:
            newState = state.map(item => {
                item.show = false;
                return item;
            });
            const dialogState: IDialogState = {
                ...action.payload,
                show: true,
                disabled: false
            };
            return [...newState, dialogState];
        case CLOSE_DIALOG:
            newState = state.splice(0, state.length - 1);
            if (newState.length > 0) {
                newState[newState.length - 1].show = true;
            }
            return newState;
        case CHANGE_DISABLED:
            return [...state.slice(0, state.length - 2), { ...state[state.length - 1], disabled: action.payload }];
        default:
            return state;
    }
};

const DialogContext = createContext<IContext>({} as IContext);

const DialogContextProvider: FC = props => {
    const [promise, setPromise] = useState<IPromise<IDialogResult> | null>(null);
    const [dialogs, dispatch] = useReducer(reducer, initialState);

    const showDialogAsync = async (dialog: IDialogProps) => {
        return new Promise<IDialogResult>(resolve => {
            setPromise({ resolve });
            dispatch({ type: SHOW_DIALOG, payload: dialog });
        });
    };
    const closeDialog = () => {
        dispatch({ type: CLOSE_DIALOG });
    };
    const changeDisabled = (disabled: boolean) => {
        dispatch({ type: CHANGE_DISABLED, payload: disabled });
    };
    const confirm = (promise: IPromise<IDialogResult> | null, payload?: any) => {
        if (promise) {
            promise.resolve({ success: true, payload });
            dispatch({ type: CLOSE_DIALOG });
        }
    };
    const cancel = (promise: IPromise<IDialogResult> | null) => {
        if (promise) {
            promise.resolve({ success: false });
        }
        dispatch({ type: CLOSE_DIALOG });
    };

    return (
        <DialogContext.Provider value={{ showDialogAsync, closeDialog }}>
            <MemoryRouter>
                {dialogs
                    ?.filter(e => e.show)
                    .map((dialog, index) => (
                        <Dialog
                            key={index}
                            title={dialog.title}
                            width={dialog.width}
                            height={dialog.height}
                            onClose={() => (dialog.customCloseAction ? dialog.customCloseAction() : cancel(promise))}
                        >
                            {React.cloneElement(dialog.component, {
                                onConfirm: payload => confirm(promise, payload),
                                onCancel: () => cancel(promise),
                                onChangeDisabled: changeDisabled
                            })}

                            <DialogActionsBar layout={dialog.buttonsLayout}>
                                {getConfimText(dialog.actionButtons) !== undefined && (
                                    <Button
                                        type="submit"
                                        themeColor={'primary'}
                                        form={DIALOG_FORM_ID}
                                        onClick={() => !dialog.isForm && confirm(promise)}
                                        disabled={dialog.disabled}
                                    >
                                        {getConfimText(dialog.actionButtons)}
                                    </Button>
                                )}
                                {getCancelText(dialog.actionButtons) !== undefined && (
                                    <Button type="button" onClick={() => cancel(promise)} disabled={dialog.disabled}>
                                        {getCancelText(dialog.actionButtons)}
                                    </Button>
                                )}
                            </DialogActionsBar>
                        </Dialog>
                    ))}
            </MemoryRouter>
            {props.children}
        </DialogContext.Provider>
    );
};

const useDialog = () => useContext(DialogContext);

export { DialogContextProvider, DialogContext, useDialog };
